import { Boxes, HardDrive, LayoutGrid, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { pluralize } from '@/react/common/string-utils';
import { useEnvironmentList } from '@/react/portainer/environments/queries/useEnvironmentList';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { useEdgeGroups } from '@/react/edge/edge-groups/queries/useEdgeGroups';

import { Datatable } from '@@/datatables';
import { Button } from '@@/buttons';
import { Icon } from '@@/Icon';
import { Card } from '@@/primitives/Card';
import { useTableState } from '@@/datatables/useTableState';
import { createPersistedStore } from '@@/datatables/types';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { StatusSummaryBar } from '@@/StatusSummaryBar/StatusSummaryBar';
import { Color } from '@@/StatusSummaryBar/FilterBarButton';

import { workflowQueryKeys } from '../../queries/query-keys';
import {
  EnvironmentReconcileStatus,
  WorkflowArtifact,
  WorkflowStatus,
} from '../types';

import { reconcileColumns, ReconcileRow } from './ReconcileStatusColumns';

const tableKey = 'gitops-reconcile-status';

const settingsStore = createPersistedStore(tableKey, 'name');

/**
 * Encodes which target a row is filtered to.
 *
 * The kind is part of the value because Edge groups and environment groups
 * number independently, so id 1 means two different things.
 */
type TargetFilter = `edge:${number}` | `group:${number}`;

const statusOptions: ReadonlyArray<{
  value: WorkflowStatus;
  label: string;
  color: Color;
}> = [
  { value: 'error', label: 'Error', color: 'error' },
  { value: 'drifted', label: 'Drifted', color: 'warning' },
  { value: 'syncing', label: 'Syncing', color: 'blue' },
  { value: 'healthy', label: 'Healthy', color: 'success' },
  { value: 'unknown', label: 'Unknown', color: 'gray' },
];

/**
 * Lists what each environment reported about a reconciled artifact.
 *
 * A table rather than a list of cards: a deployment can target hundreds of
 * environments, and when something is wrong they usually report the same thing,
 * so the useful questions are "how many" and "which ones" - which need counting,
 * filtering and paging rather than scrolling.
 */
export function ReconcileStatusSection({
  workflowId,
  artifacts,
}: {
  workflowId: number;
  artifacts: Array<WorkflowArtifact>;
}) {
  const reconciled = useMemo(
    () => artifacts.filter((artifact) => artifact.type === 'gitopsDeployment'),
    [artifacts]
  );

  if (!reconciled.length) {
    return null;
  }

  return (
    <ReconcileEnvironmentsTable
      workflowId={workflowId}
      artifacts={reconciled}
    />
  );
}

/**
 * One table for the whole workflow rather than one per artifact.
 *
 * Every artifact of a reconciled workflow is published to the same fleet on the
 * same schedule, so a table each repeats the environment list, the status bar
 * and the target filter once per artifact, and answers "is this workflow
 * healthy" only by reading them all. Merged, the question the page exists for
 * is one row of counts, and the artifact becomes a column like any other.
 */
function ReconcileEnvironmentsTable({
  workflowId,
  artifacts,
}: {
  workflowId: number;
  artifacts: Array<WorkflowArtifact>;
}) {
  const tableState = useTableState(settingsStore, tableKey);

  // Component state rather than a URL parameter: the filters belong to this
  // table rather than to the route, and clear with it.
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus>();
  const [targetFilter, setTargetFilter] = useState<TargetFilter>();
  const [artifactFilter, setArtifactFilter] = useState<number>();

  // An environment reports once per artifact, so a row is the pair. Carrying the
  // artifact on the row is what lets one table serve all of them.
  const reports = useMemo(
    () =>
      artifacts.flatMap((artifact) =>
        (artifact.environments ?? []).map((environment) => ({
          environment,
          artifact,
        }))
      ),
    [artifacts]
  );

  // One request for every environment in the table rather than one per row,
  // which is the difference between a page load and a stampede at fleet size.
  const endpointIds = useMemo(
    () => [
      ...new Set(reports.map(({ environment }) => environment.endpointId ?? 0)),
    ],
    [reports]
  );

  const { environments: known } = useEnvironmentList(
    { endpointIds, pageLimit: 0 },
    { enabled: endpointIds.length > 0 }
  );

  // Narrowed to the chosen artifact and target but not yet to a status: the
  // status bar counts these, so that picking a status never changes the numbers
  // beside it.
  const targeted = useMemo(() => {
    const groupIds = new Map(known.map((env) => [env.Id, env.GroupId]));

    return reports.filter(
      ({ environment, artifact }) =>
        (!artifactFilter || artifact.id === artifactFilter) &&
        matchesTarget(environment, targetFilter, groupIds)
    );
  }, [reports, known, targetFilter, artifactFilter]);

  const dataset: Array<ReconcileRow> = useMemo(() => {
    const names = new Map(known.map((env) => [env.Id, env.Name]));

    return targeted
      .filter(
        ({ environment }) =>
          !statusFilter || environment.status === statusFilter
      )
      .map(({ environment, artifact }) => ({
        ...environment,
        // Unique per row rather than per environment: the same environment
        // appears once for each artifact it reports on.
        id: environment.endpointId ?? 0,
        name:
          names.get(environment.endpointId ?? 0) ??
          `Environment ${environment.endpointId}`,
        workflowId,
        deploymentId: artifact.id,
        deploymentName: artifact.name,
        helm: artifact.helm,
      }));
  }, [known, statusFilter, targeted, workflowId]);

  const counts = useMemo(() => {
    const byStatus = new Map<WorkflowStatus, number>();
    targeted.forEach(({ environment }) =>
      byStatus.set(
        environment.status,
        (byStatus.get(environment.status) ?? 0) + 1
      )
    );

    return byStatus;
  }, [targeted]);

  const label = 'Environments';

  const targetOptions = useTargetOptions(artifacts);
  const showArtifact = artifacts.length > 1;

  return (
    // Card rather than the widget the table brings by default: the widget is
    // inset by mx-4 and sits on a lighter surface, which leaves this out of
    // line with the cards above it.
    <Card.Container aria-label={label}>
      <Datatable<ReconcileRow>
        noWidget
        columns={reconcileColumns(showArtifact)}
        dataset={dataset}
        settingsManager={tableState}
        // A node rather than a string, so the heading matches Card.Header
        // instead of the table's larger text and circular icon badge. The
        // accessible name comes from ariaLabel below.
        title={
          <span className="flex items-center gap-2">
            <Icon
              icon={HardDrive}
              size="md"
              className="shrink-0 opacity-70 th-highcontrast:text-white th-dark:text-gray-3"
            />
            <span className="text-sm leading-tight font-extrabold tracking-wide text-gray-9 th-highcontrast:text-white th-dark:text-white">
              {label}
            </span>
          </span>
        }
        ariaLabel={label}
        disableSelect
        renderTableActions={() => <RefreshButton />}
        data-cy={`workflow-reconcile-status-${workflowId}`}
        description={
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <EdgeGroups ids={targetIds(artifacts, 'edgeGroupIds')} />
              <EnvironmentGroups
                ids={targetIds(artifacts, 'environmentGroupIds')}
              />
              <NamedEnvironments ids={targetIds(artifacts, 'environmentIds')} />

              {/* A definite width: a proportional one collapses to a few
                  pixels when the description row is narrow. */}
              <div className="ml-auto flex items-center gap-2">
                {showArtifact && (
                  <div className="w-48">
                    <PortainerSelect<number | undefined>
                      isClearable
                      bindToBody
                      placeholder="Filter by artifact"
                      value={artifactFilter}
                      onChange={(value) =>
                        setArtifactFilter(value ?? undefined)
                      }
                      options={artifacts.map((artifact) => ({
                        value: artifact.id,
                        label: artifact.name,
                      }))}
                      data-cy="workflow-reconcile-artifact-filter"
                    />
                  </div>
                )}

                {targetOptions.length > 1 && (
                  <div className="w-64">
                    <PortainerSelect<TargetFilter | undefined>
                      isClearable
                      bindToBody
                      placeholder="Filter by target"
                      value={targetFilter}
                      onChange={(value) => setTargetFilter(value ?? undefined)}
                      options={targetOptions}
                      data-cy="workflow-reconcile-target-filter"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sized to its own counts. As a flex-column child it otherwise
                stretches to whatever the row above it measures, so naming the
                targets made the status bar grow with them. */}
            <div className="max-w-full self-start">
              <StatusSummaryBar<WorkflowStatus>
                total={targeted.length}
                segments={statusOptions.map((option) => ({
                  key: option.value,
                  label: option.label,
                  count: counts.get(option.value) ?? 0,
                  color: option.color,
                }))}
                value={statusFilter ?? null}
                onChange={(value) => setStatusFilter(value ?? undefined)}
                radioGroupName={`workflow-reconcile-status-${workflowId}`}
                ariaLabel="Filter by status"
                data-cy="workflow-reconcile-status-filter"
              />
            </div>
          </div>
        }
      />
    </Card.Container>
  );
}

/**
 * Names the Edge groups the deployment targets, since the table lists the
 * environments they resolve to and not the groups themselves.
 */
function EdgeGroups({ ids }: { ids: Array<number> | undefined }) {
  const { data: groups } = useEdgeGroups();

  if (!ids?.length) {
    return null;
  }

  const names = ids.map(
    (id) => groups?.find((group) => group.Id === id)?.Name ?? `Edge Group ${id}`
  );

  return (
    <GroupNames
      icon={LayoutGrid}
      label={pluralize(names.length, 'Edge group')}
      names={names}
    />
  );
}

/**
 * Names the groups the deployment targets, since the table lists environments
 * and not the groups that put them there.
 */
function EnvironmentGroups({ ids }: { ids: Array<number> | undefined }) {
  const { data: groups } = useGroups();

  if (!ids?.length) {
    return null;
  }

  const names = ids.map(
    (id) => groups?.find((group) => group.Id === id)?.Name ?? `Group ${id}`
  );

  return (
    <GroupNames
      icon={Boxes}
      label={pluralize(names.length, 'Group')}
      names={names}
    />
  );
}

/**
 * Counts the environments the deployment was pointed at directly.
 *
 * An artifact can name environments as well as groups, and one that names only
 * environments has no group to describe it, which left the header empty. A
 * count rather than the names: the table below is that list, and an environment
 * name is often a UUID.
 */
function NamedEnvironments({ ids }: { ids: Array<number> }) {
  if (!ids.length) {
    return null;
  }

  return (
    <span className="flex items-center gap-1.5 text-xs">
      <Icon icon={HardDrive} size="xs" className="shrink-0 text-gray-6" />
      <span className="text-muted">Named:</span>
      {`${ids.length} ${pluralize(ids.length, 'environment')}`}
    </span>
  );
}

/** How many group names are spelled out before the rest are counted. */
const MAX_NAMED_GROUPS = 3;

/**
 * One kind of target, named the same way the targets card names it.
 *
 * A deployment can target many groups, and a description row is one line, so
 * the first few are named and the rest counted, with the full list on hover.
 */
function GroupNames({
  icon,
  label,
  names,
}: {
  icon: typeof Boxes;
  label: string;
  names: Array<string>;
}) {
  const named = names.slice(0, MAX_NAMED_GROUPS).join(', ');
  const rest = names.length - MAX_NAMED_GROUPS;

  return (
    <span
      className="flex items-center gap-1.5 text-xs"
      title={names.join(', ')}
    >
      <Icon icon={icon} size="xs" className="shrink-0 text-gray-6" />
      <span className="text-muted">{label}:</span>
      {rest > 0 ? `${named} +${rest} more` : named}
    </span>
  );
}

/**
 * Lists the targets a deployment can be filtered to.
 *
 * Grouped by kind rather than flattened: a fleet can have an Edge group and an
 * environment group of the same name, and the two mean different things.
 */
function useTargetOptions(artifacts: Array<WorkflowArtifact>) {
  const { data: edgeGroups } = useEdgeGroups();
  const { data: groups } = useGroups();

  return useMemo(() => {
    const edgeGroupIds = targetIds(artifacts, 'edgeGroupIds');
    const environmentGroupIds = targetIds(artifacts, 'environmentGroupIds');
    const options = [];

    if (edgeGroupIds?.length) {
      options.push({
        label: 'Edge groups',
        options: edgeGroupIds.map((id) => ({
          value: `edge:${id}` as TargetFilter,
          label:
            edgeGroups?.find((group) => group.Id === id)?.Name ??
            `Edge Group ${id}`,
        })),
      });
    }

    if (environmentGroupIds?.length) {
      options.push({
        label: 'Environment groups',
        options: environmentGroupIds.map((id) => ({
          value: `group:${id}` as TargetFilter,
          label:
            groups?.find((group) => group.Id === id)?.Name ?? `Group ${id}`,
        })),
      });
    }

    return options;
  }, [artifacts, edgeGroups, groups]);
}

/**
 * The targets of one kind across every artifact, listed once.
 *
 * A workflow's artifacts usually name the same groups, and the table covers
 * them all, so naming a group once is what the header is for.
 */
function targetIds(
  artifacts: Array<WorkflowArtifact>,
  kind: 'edgeGroupIds' | 'environmentGroupIds' | 'environmentIds'
): Array<number> {
  return [
    ...new Set(artifacts.flatMap((artifact) => artifact.target?.[kind] ?? [])),
  ];
}

/**
 * Reports whether an environment was reached through the filtered target.
 *
 * Edge group membership is reported by the server, which is the only place the
 * rules are evaluated. An environment group is read off the environment, which
 * belongs to exactly one.
 */
function matchesTarget(
  environment: EnvironmentReconcileStatus,
  filter: TargetFilter | undefined,
  groupIds: Map<number, number>
): boolean {
  if (!filter) {
    return true;
  }

  const [kind, id] = filter.split(':');

  if (kind === 'edge') {
    return !!environment.edgeGroupIds?.includes(Number(id));
  }

  return groupIds.get(environment.endpointId ?? 0) === Number(id);
}

/**
 * Re-reads what the environments have reported. Distinct from reconciling,
 * which asks them to apply again: this only fetches what the server already
 * knows, for watching a reconcile land.
 */
function RefreshButton() {
  const queryClient = useQueryClient();

  return (
    <Button
      color="light"
      size="xsmall"
      icon={RefreshCw}
      onClick={() =>
        queryClient.invalidateQueries({ queryKey: workflowQueryKeys.all })
      }
      data-cy="workflow-reconcile-refresh"
    >
      Refresh
    </Button>
  );
}
